import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessor } from '@ngneat/reactive-forms';
import { v7 as uuid } from 'uuid';

export interface Radiobutton {
  id: string;
  label: string;
  value?: string;
  descriptionInnerHTML?: string;
  disabled?: boolean;
}

@Component({
  selector: 'lib-vinnova-radiobuttons',
  templateUrl: './radiobuttons.component.html',
  styleUrls: ['./radiobuttons.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadiobuttonsComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RadiobuttonsComponent implements ControlValueAccessor, OnChanges {
  private readonly focusVisible = 'focus-visible';

  @ViewChild('inputGroup') inputGroupRef: ElementRef;
  @Input() radiobuttonObjects: Radiobutton[];
  @Input() horizontal: boolean;
  @Input() error: boolean;
  @Input() required: boolean;
  @Input() name: string;
  @Input() radioId = '';
  @Input() labelPrefix = '';
  @Input() set disabled(value: boolean) {
    this.setDisabledState(value);
  }
  // Large is default size.
  @Input() size: 'small' | 'medium';
  @Input() labelledBy: string;
  @Input() booleanOptions: boolean;
  @Input() trueLabel: string;
  @Input() falseLabel: string;
  @Input() falseTop: boolean;
  @Input() showChangeIndicator: boolean;

  onChange?: (value: string | boolean) => void;
  onTouched?: () => void;
  mousedown: boolean;
  isDisabled: boolean;

  constructor(
    private cdr: ChangeDetectorRef,
    public elementRef: ElementRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['booleanOptions'] ||
      changes['trueLabel'] ||
      changes['falseLabel'] ||
      changes['falseTop']
    ) {
      this.setBooleanLabels();
    }
  }

  writeValue(value): void {
    this.radioId = this.booleanOptions
      ? this.getRadioIdFromValue(value?.toString())
      : (value ?? '');
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  updateValue(event) {
    if (event.target.checked) {
      this.radioId = event.target.dataset.value;

      if (this.booleanOptions) {
        this.onChange(event.target.value === 'true');
      } else {
        this.onChange(this.radioId);
      }
    }
  }

  setBooleanLabels() {
    this.radiobuttonObjects = [
      {
        id: uuid(),
        label: this.trueLabel,
        value: 'true',
      },
      {
        id: uuid(),
        label: this.falseLabel,
        value: 'false',
      },
    ];
    if (this.falseTop) {
      this.radiobuttonObjects.sort((a) => (a.value === 'true' ? 1 : -1));
    }
  }

  @HostListener('mousedown', ['$event'])
  onMousedown(event) {
    this.mousedown = true;
    const input = document.activeElement;
    if (!input.classList.contains('focus-visible')) {
      return;
    }
    const label =
      document.activeElement.parentElement.parentElement.querySelector('label');
    if (input === event.target || label === event.target) {
      this.removeClassFocusVisible(document.activeElement);
    }
  }

  onFocus(event) {
    if (!this.mousedown) {
      event.target.classList.add(this.focusVisible);
    }
    this.mousedown = false;
  }

  @HostListener('keydown.enter', ['$event'])
  @HostListener('keydown.space', ['$event'])
  onKeyEnter(event) {
    event.preventDefault();
    const radio = this.getRadioById(document.activeElement.id);

    if (radio !== null && !radio.checked) {
      radio.checked = !radio.checked;
      this.updateValue(event);
    }
  }

  @HostListener('keydown.arrowright', ['$event'])
  @HostListener('keydown.arrowdown', ['$event'])
  onKeyNext(event) {
    event.preventDefault();
    const nextSibling =
      document.activeElement.parentElement.parentElement.nextSibling;
    this.onArrowUpOrDown(nextSibling as HTMLElement);
  }

  @HostListener('keydown.arrowleft', ['$event'])
  @HostListener('keydown.arrowup', ['$event'])
  onKeyPrev(event) {
    event.preventDefault();
    const previousSibling =
      document.activeElement.parentElement.parentElement.previousSibling;
    this.onArrowUpOrDown(previousSibling as HTMLElement);
  }

  onBlur(event) {
    event.target.classList.remove(this.focusVisible);
    this.onTouched();
  }

  onArrowUpOrDown(sibling: HTMLElement) {
    if (sibling != undefined) {
      const radio = sibling.querySelector('input') as HTMLElement;
      if (this.activeElementCheck(radio)) {
        radio.focus();
        radio.classList.add(this.focusVisible);
      }
    }
  }

  removeClassFocusVisible(activeElement: Element) {
    if (this.activeElementCheck(activeElement)) {
      this.getRadioById(activeElement.id).classList.remove(this.focusVisible);
    }
  }

  activeElementCheck(activeElement: Element): boolean {
    return (
      activeElement?.id?.includes('id') &&
      this.inputGroupRef.nativeElement.contains(activeElement)
    );
  }

  getRadioById(id: string) {
    return this.inputGroupRef.nativeElement.querySelector(`#${id}`);
  }

  getRadioIdFromValue(value: string) {
    return this.radiobuttonObjects.find((a) => a.value === value)?.id;
  }

  trackByFn(index) {
    return index;
  }
}
